import { createClient } from '@supabase/supabase-js';

// These environment variables are automatically injected by Lovable
const supabaseUrl = 'https://eyedvzmodfhyrxnhhrvf.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV5ZWR2em1vZGZoeXJ4bmhocnZmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzgwMDg2MzQsImV4cCI6MjA1MzU4NDYzNH0.GbmvOgvpgqWXjGnpK2HfRgPPAdu6rRph1kqS6YfTJu8';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    debug: true,  // Enable debug logs to help troubleshoot auth issues
    flowType: 'pkce', // Use PKCE flow for better security and email verification
  }
});

// Debug helper to check auth state
export const checkAuthState = async () => {
  const { data, error } = await supabase.auth.getSession();
  console.log('Auth state:', { data, error });
  return data.session;
};

// Completely revamped logout function with multiple fallback strategies
export const logoutUser = async () => {
  console.log('Starting enhanced logout process...');
  
  // Step 1: Try to get the current session first
  let activeSession = false;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    activeSession = !!sessionData.session;
    console.log('Session check:', activeSession ? 'Active session found' : 'No active session');
  } catch (e) {
    console.error('Error checking session:', e);
  }
  
  // Step 2: Apply a comprehensive logout strategy with fallbacks
  try {
    // First try the standard way
    if (activeSession) {
      console.log('Attempting standard signOut...');
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.warn('Standard signOut error:', error);
          throw error; // Let the fallback handle it
        } else {
          console.log('Standard signOut successful');
        }
      } catch (e) {
        // Try with global scope if standard fails
        console.log('Trying global scope signOut...');
        const { error: globalError } = await supabase.auth.signOut({ 
          scope: 'global' 
        });
        
        if (globalError) {
          console.warn('Global signOut error:', globalError);
          throw globalError; // Move to next fallback
        } else {
          console.log('Global signOut successful');
        }
      }
    } else {
      console.log('No active session, skipping API signOut calls');
    }
  } catch (signOutError) {
    console.error('All signOut attempts failed:', signOutError);
  }
  
  // Step 3: Manual cleanup as final fallback - ALWAYS do this
  console.log('Performing manual token cleanup');
  
  // Clear all auth data from localStorage
  try {
    // Target specific Supabase auth tokens
    const keys = [
      'supabase.auth.token',
      'supabase.auth.refreshToken',
      'sb-refresh-token',
      'sb-access-token',
      'supabase.auth.expires_at',
      'supabase-auth-token'
    ];
    
    // Remove specific keys
    keys.forEach(key => {
      if (localStorage.getItem(key)) {
        console.log(`Removing ${key} from localStorage`);
        localStorage.removeItem(key);
      }
    });
    
    // Also scan for any keys that might be related to auth
    Object.keys(localStorage).forEach(key => {
      if (key.includes('supabase') || key.includes('sb-') || key.includes('auth')) {
        console.log(`Removing detected auth key: ${key}`);
        localStorage.removeItem(key);
      }
    });
    
    // Also handle sessionStorage for good measure
    Object.keys(sessionStorage || {}).forEach(key => {
      if (key.includes('supabase') || key.includes('sb-') || key.includes('auth')) {
        console.log(`Removing auth key from sessionStorage: ${key}`);
        sessionStorage.removeItem(key);
      }
    });
    
    console.log('Storage cleanup completed');
  } catch (e) {
    console.error('Error during storage cleanup:', e);
  }
  
  // Step 4: Update internal Supabase state if possible
  try {
    // Force Supabase internal auth store to reset
    // @ts-ignore - accessing internal property
    if (supabase.auth && supabase.auth.storage) {
      // @ts-ignore - force internal reset
      supabase.auth.storage.removeItem('supabase.auth.token');
    }
  } catch (e) {
    console.error('Error resetting internal auth state:', e);
  }
  
  console.log('Logout process completed with all fallbacks applied');
  
  return { success: true, manualCleanup: true };
};
